import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import AppSelect from 'components/AppSelect';
import { useQuery } from '@tanstack/react-query';
import apiReq from 'core/api';

interface Specification {
  id: string;
  name: string;
  created_at: string;
}

interface SpecificationSelectProps {
  projectId: string | number | null;
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}

const SpecificationSelect: React.FC<SpecificationSelectProps> = ({
  projectId,
  value,
  onChange,
  disabled = false,
}) => {
  const { data: specifications = [], isLoading } = useQuery({
    queryKey: ['specifications-history', projectId],
    queryFn: () => apiReq(`projects/specifications-history?project_id=${projectId}`),
    enabled: !!projectId,
  });

  const selectedSpecification = specifications.find((spec: Specification) => spec.id === value);

  const handleChange = (event: React.SyntheticEvent, newValue: Specification | null) => {
    onChange(newValue?.id || null);
  };

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (value && specifications.length > 0) {
      const specExists = specifications.find((spec: Specification) => spec.id === value);
      if (!specExists) {
        onChangeRef.current(null);
      }
    }
  }, [specifications, value]);

  return (
    <Box>
      <Typography
        variant="caption"
        style={{
          display: 'block',
          marginBottom: 8,
          color: 'text.secondary',
          fontSize: '0.75rem',
        }}
      >
        Выберите ТЗ:
      </Typography>
      <AppSelect<Specification>
        size="small"
        options={specifications}
        value={selectedSpecification || null}
        onChange={handleChange}
        getOptionLabel={(option: Specification) => option.name || `ТЗ #${option.id}`}
        isOptionEqualToValue={(option: Specification, value: Specification) =>
          option.id === value.id
        }
        loading={isLoading}
        disabled={disabled}
        clearOnBlur={false}
        textFieldProps={{ placeholder: isLoading ? 'Загрузка...' : 'Выберите ТЗ' }}
        renderOption={(props, option: Specification) => (
          <Box component="li" {...props}>
            <Box>
              <Typography variant="body2" component="div">
                {option.name || `ТЗ #${option.id}`}
              </Typography>
            </Box>
          </Box>
        )}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

export default SpecificationSelect;
